import React, { useState, useEffect } from 'react';
import { Pagination } from 'react-bootstrap';

/**
 * TabelaDinamica - Componente de tabela reutilizável
 *
 * Props:
 * @param {Array} colunas - Array de objetos com configuração das colunas
 *   - title: string - Título da coluna
 *   - key: string - Chave para acessar o valor no objeto de dados
 *   - ordenacao: boolean - Se a coluna pode ser ordenada
 *   - width: string - Largura fixa (opcional, ex: '200px')
 *   - fontSize: string - Tamanho do texto (opcional, ex: '14px')
 *   - align: string - Alinhamento (left, center, right)
 *   - render: function(value, item) - Render customizado (opcional)
 *
 * @param {Array} dados - Array de objetos com os dados da tabela
 * @param {boolean} action - Se deve exibir coluna de ações
 * @param {Array} acoes - Array de objetos com as ações disponíveis
 *   - label: string - Texto do botão
 *   - icon: string/element - Ícone (opcional)
 *   - onClick: function - Função executada ao clicar (recebe o item como parâmetro)
 *   - type: string - Tipo do botão ('danger', 'success' ou padrão azul)
 *   - style: object - Estilos customizados (opcional)
 *
 * @param {string} fontSize - Tamanho padrão do texto da tabela
 * @param {string} actionColumnWidth - Largura da coluna de ações
 * @param {string} headerBgColor - Cor de fundo do cabeçalho
 * @param {boolean} striped - Linhas alternadas com cores diferentes
 * @param {boolean} hover - Efeito hover nas linhas
 * @param {boolean} card - Envolve a tabela em um card com header (default: false)
 * @param {string} headerTitle - Título exibido no header do card (usado quando card=true)
 * @param {object} cardStyle - Estilos customizados para o container do card (opcional)
 * @param {function} linhaSelecionada - Função (item) => boolean que indica se a linha está selecionada
 */

const styles = {
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    color: '#ffffff',
  },
  thSortable: {
    cursor: 'pointer',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #e0e0e0',
  },
  sortIcon: {
    marginLeft: '8px',
    opacity: 0.5,
  },
  sortIconActive: {
    marginLeft: '8px',
    opacity: 1,
  },
  actionButton: {
    padding: '6px 12px',
    marginRight: '4px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: '#ffffff',
    fontSize: '12px',
    transition: 'background-color 0.2s',
  },
  actionButtonDanger: {
    backgroundColor: '#dc3545',
  },
  actionButtonSuccess: {
    backgroundColor: '#28a745',
  },
  actionsCell: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  emptyRow: {
    textAlign: 'center',
    padding: '24px',
    color: '#666',
  },
};

const BUTTON_TYPE_STYLES = {
  danger: styles.actionButtonDanger,
  success: styles.actionButtonSuccess,
};

function Tabela({
  colunas = [],
  dados = [],
  action = false,
  acoes = [],
  fontSize: fontSizeDefault = '14px',
  actionColumnWidth = 'auto',
  headerBgColor = '#0747a6',
  striped = true,
  hover = true,
  card = false,
  headerTitle = '',
  cardStyle = {},
  cliqueLinha,
  linhaSelecionada,
  ordenacao: ordenacaoProp,
  onOrdenacaoChange,
  cellPadding = '12px 16px',
  headerPadding = '12px 16px',
  cellLineHeight = 'normal',
  headerLineHeight = 'normal',
  cellWhiteSpace = 'normal',
  headerWhiteSpace = 'nowrap',
  verticalAlign = 'middle',
  tableLayout = 'auto',
  minWidth = '100%',
  paginar = false,
  itensPorPagina = 20,
}) {
  const [ordenacaoInterna, setOrdenacaoInterna] = useState({ coluna: null, direcao: 'asc' });
  const ordenacaoEhControlada =
    ordenacaoProp != null && typeof onOrdenacaoChange === 'function';
  const ordenacao = ordenacaoEhControlada ? ordenacaoProp : ordenacaoInterna;
  const [hoverRow, setHoverRow] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    var mq = window.matchMedia('(max-width: 576px)');
    var handler = function(e) {
      setIsMobile(e.matches);
    };
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return function() {
      mq.removeEventListener('change', handler);
    };
  }, []);

  function handleOrdenacao(coluna) {
    if (!coluna.ordenacao) return;

    const novaOrdenacao = {
      coluna: coluna.key,
      direcao:
        ordenacao.coluna === coluna.key && ordenacao.direcao === 'asc'
          ? 'desc'
          : 'asc',
    };

    if (ordenacaoEhControlada) {
      onOrdenacaoChange(novaOrdenacao);
    } else {
      setOrdenacaoInterna(novaOrdenacao);
    }
  }

  function ordenarDados(dadosParaOrdenar) {
    if (ordenacaoEhControlada) return dadosParaOrdenar;
    if (!ordenacao.coluna) return dadosParaOrdenar;

    const colunaConfig = colunas.find(col => col.key === ordenacao.coluna);
    if (!colunaConfig || !colunaConfig.ordenacao) return dadosParaOrdenar;

    return [...dadosParaOrdenar].sort((a, b) => {
      let valorA = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
      let valorB = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';

      if (typeof valorA === 'number' && typeof valorB === 'number') {
        return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
      }

      const strA = String(valorA).toLowerCase();
      const strB = String(valorB).toLowerCase();

      if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  }

  function renderSortIcon(coluna) {
    if (!coluna.ordenacao) return null;

    const isActive = ordenacao.coluna === coluna.key;
    const icon = isActive ? (ordenacao.direcao === 'asc' ? '↑' : '↓') : '↕';

    return (
      <span style={isActive ? styles.sortIconActive : styles.sortIcon}>
        {icon}
      </span>
    );
  }

  function renderCellValue(item, coluna) {
    const value = item[coluna.key];

    if (typeof coluna.render === 'function') return coluna.render(value, item);
    if (value == null) return '-';
    return value;
  }

  function getThStyle(coluna) {
    return {
      ...styles.th,
      backgroundColor: headerBgColor,
      width: coluna.width || 'auto',
      padding: coluna.headerPadding || headerPadding,
      fontSize: coluna.fontSize || fontSizeDefault,
      lineHeight: coluna.headerLineHeight || headerLineHeight,
      whiteSpace: coluna.headerWhiteSpace || headerWhiteSpace,
      textAlign: coluna.align || 'left',
      ...(coluna.ordenacao ? styles.thSortable : {}),
    };
  }

  function getRowStyle(rowIndex, item) {
    const isSelected =
      typeof linhaSelecionada === 'function' && linhaSelecionada(item);
    const isStriped = striped && rowIndex % 2 === 1;
    const isHovered = hover && hoverRow === rowIndex;

    if (isSelected) return { backgroundColor: '#cce5ff' };
    if (isHovered) return { backgroundColor: '#e9ecef' };
    if (isStriped) return { backgroundColor: '#f8f9fa' };
    return {};
  }

  function getButtonStyle(acao) {
    return {
      ...styles.actionButton,
      ...(BUTTON_TYPE_STYLES[acao.type] || {}),
      ...(acao.style || {}),
    };
  }

  const totalColunas = action ? colunas.length + 1 : colunas.length;
  const dadosOrdenados = ordenarDados(dados);
  const totalPaginas = paginar
    ? Math.max(1, Math.ceil(dadosOrdenados.length / itensPorPagina))
    : 1;
  useEffect(() => {
    setPaginaAtual(1);
  }, [dados, itensPorPagina, ordenacao.coluna, ordenacao.direcao]);
  const paginaCorrigida = Math.min(paginaAtual, totalPaginas);
  const dadosExibidos = paginar
    ? dadosOrdenados.slice(
        (paginaCorrigida - 1) * itensPorPagina,
        paginaCorrigida * itensPorPagina
      )
    : dadosOrdenados;

  const tableElement = (
    <table
      style={{
        ...styles.table,
        tableLayout,
        minWidth,
      }}
    >
      <thead>
        <tr>
          {colunas.map((coluna, index) => (
            <th
              key={coluna.key || index}
              style={getThStyle(coluna)}
              onClick={() => handleOrdenacao(coluna)}
            >
              {coluna.title}
              {renderSortIcon(coluna)}
            </th>
          ))}
          {action && (
            <th
              style={{
                ...styles.th,
                backgroundColor: headerBgColor,
                width: actionColumnWidth,
                padding: headerPadding,
                fontSize: fontSizeDefault,
                lineHeight: headerLineHeight,
                whiteSpace: headerWhiteSpace,
                textAlign: 'center',
              }}
            >
              +
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {dadosOrdenados.length === 0 ? (
          <tr>
            <td colSpan={totalColunas} style={styles.emptyRow}>
              Nenhum registro encontrado
            </td>
          </tr>
        ) : (
          dadosExibidos.map((item, rowIndex) => (
            <tr
              key={item.id || rowIndex}
              style={{
                ...getRowStyle(rowIndex, item),
                ...(cliqueLinha ? { cursor: 'pointer' } : {}),
              }}
              onMouseEnter={() => setHoverRow(rowIndex)}
              onMouseLeave={() => setHoverRow(null)}
              onClick={() => cliqueLinha && cliqueLinha(item)}
            >
              {colunas.map((coluna, colIndex) => (
                <td
                  key={coluna.key || colIndex}
                  style={{
                    ...styles.td,
                    width: coluna.width || 'auto',
                    padding: coluna.cellPadding || cellPadding,
                    fontSize: coluna.fontSize || fontSizeDefault,
                    lineHeight: coluna.cellLineHeight || cellLineHeight,
                    whiteSpace: coluna.cellWhiteSpace || cellWhiteSpace,
                    verticalAlign,
                    textAlign: coluna.align || 'left',
                  }}
                >
                  {renderCellValue(item, coluna)}
                </td>
              ))}
              {action && (
                <td
                  style={{
                    ...styles.td,
                    padding: cellPadding,
                    lineHeight: cellLineHeight,
                    verticalAlign,
                    textAlign: 'center',
                  }}
                >
                  <div style={styles.actionsCell}>
                    {acoes.map((acao, acaoIndex) => {
                      if (typeof acao.hidden === 'function' && acao.hidden(item)) return null;
                      return (
                        <button
                          key={acaoIndex}
                          style={getButtonStyle(acao)}
                          onClick={() => acao.onClick(item)}
                          title={acao.title || acao.label}
                        >
                          {acao.icon && (
                            <span style={{ marginRight: acao.label ? '4px' : '0' }}>
                              {acao.icon}
                            </span>
                          )}
                          {acao.label}
                        </button>
                      );
                    })}
                  </div>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  var mobileElement = (
    <div>
      {dadosOrdenados.length === 0 ? (
        <div style={styles.emptyRow}>Nenhum registro encontrado</div>
      ) : (
        dadosExibidos.map(function(item, rowIndex) {
          var isSelected =
            typeof linhaSelecionada === 'function' && linhaSelecionada(item);
          return (
            <div
              key={item.id || rowIndex}
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '8px',
                backgroundColor: isSelected ? '#cce5ff' : '#fff',
                cursor: cliqueLinha ? 'pointer' : 'default',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
              onClick={function() {
                cliqueLinha && cliqueLinha(item);
              }}
            >
              {colunas.map(function(coluna, colIndex) {
                return (
                  <div
                    key={coluna.key || colIndex}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 0',
                      fontSize: coluna.fontSize || fontSizeDefault,
                      borderBottom:
                        colIndex < colunas.length - 1
                          ? '1px solid #f0f0f0'
                          : 'none',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: '#555',
                        marginRight: '8px',
                      }}
                    >
                      {coluna.title}
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      {renderCellValue(item, coluna)}
                    </span>
                  </div>
                );
              })}
              {action && acoes.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                  }}
                >
                  {acoes.map(function(acao, acaoIndex) {
                    if (typeof acao.hidden === 'function' && acao.hidden(item)) return null;
                    return (
                      <button
                        key={acaoIndex}
                        style={getButtonStyle(acao)}
                        onClick={function(e) {
                          e.stopPropagation();
                          acao.onClick(item);
                        }}
                        title={acao.label}
                      >
                        {acao.icon && (
                          <span
                            style={{ marginRight: acao.label ? '4px' : '0' }}
                          >
                            {acao.icon}
                          </span>
                        )}
                        {acao.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  var paginacaoElement =
    paginar && totalPaginas > 1 ? (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "12px",
          padding: "8px 16px",
        }}
      >
        <span style={{ fontSize: "13px", color: "#555" }}>
          {dadosOrdenados.length} registro(s) - pagina {paginaCorrigida} de {totalPaginas}
        </span>
        <Pagination className="mb-0">
          <Pagination.First disabled={paginaCorrigida === 1} onClick={() => setPaginaAtual(1)} />
          <Pagination.Prev disabled={paginaCorrigida === 1} onClick={() => setPaginaAtual(paginaCorrigida - 1)} />
          <Pagination.Next disabled={paginaCorrigida === totalPaginas} onClick={() => setPaginaAtual(paginaCorrigida + 1)} />
          <Pagination.Last disabled={paginaCorrigida === totalPaginas} onClick={() => setPaginaAtual(totalPaginas)} />
        </Pagination>
      </div>
    ) : null;

  var contentElement = (
    <div>
      {isMobile ? mobileElement : tableElement}
      {paginacaoElement}
    </div>
  );

  if (card) {
    var adjustedCardStyle = isMobile
      ? {
          ...cardStyle,
          height: 'auto',
          maxHeight: cardStyle.maxHeight || '50vh',
        }
      : cardStyle;

    return (
      <div
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.125)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: 0,
          minWidth: '100%',
          ...adjustedCardStyle,
        }}
      >
        <div
          style={{
            color: '#fff',
            background: headerBgColor,
            padding: '0.75rem 1.25rem',
            flexShrink: 0,
          }}
        >
          <h5 style={{ marginBottom: 0 }}>{headerTitle}</h5>
        </div>
        <div
          style={{
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            flex: 1,
            minHeight: 0,
          }}
        >
          {contentElement}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '0px 0px 8px 8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        width: 0,
        minWidth: '100%',
      }}
    >
      {contentElement}
    </div>
  );
}

export default Tabela;
